import React, { useCallback, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import CommentIcon from '@atlaskit/icon/core/comment';
import { type AnnotationId, AnnotationTypes } from '@atlaskit/adf-schema/annotation';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import type { AnnotationPlugin } from '@atlaskit/editor-plugin-annotation/annotationPluginType';
import type { MediaPlugin } from '@atlaskit/editor-plugin-media/media-plugin-type';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import type { MediaViewerExtensions } from '@atlaskit/media-viewer/types';
import type { Identifier } from '@atlaskit/media-client/identifier';
import type { MediaNodeContext } from '@atlaskit/renderer/actions';

import {
	getCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { getProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';

import { getOccurrenceKey, occurrenceKeysMatch } from '../utils/mediaOccurrenceKey.ts';

import {
	getMediaViewerSidebarState,
	resetMediaViewerSidebar,
	setMediaViewerOpenFromIdentifier,
	setMediaViewerSelectedIdentifier,
	setMediaViewerSidebarOpen,
} from './mediaViewerSidebarStore.ts';

export type MediaTargetResolution =
	// `isInMediaSingle` is false for media in a file card, such as a zip, which can't be commented on.
	| { status: 'resolved'; target: MediaNodeContext; isInMediaSingle?: boolean }
	| { status: 'missing' | 'ambiguous' | 'unsupported' };

export type ApplyMediaAnnotation = (
	target: MediaNodeContext,
	annotationId: string,
) => Promise<boolean> | boolean;

export type RemoveMediaAnnotation = (annotationId: string) => Promise<boolean> | boolean;

export type MediaViewerCommentSidebarProps = {
	resolveTarget: (identifier?: Identifier) => MediaTargetResolution;
	keepLastResolvedTarget?: boolean;
	selectedIdentifier?: Identifier;
	applyMediaAnnotation?: ApplyMediaAnnotation;
	removeMediaAnnotation?: RemoveMediaAnnotation;
};

export type MediaViewerCommentSidebarComponent =
	React.ComponentType<MediaViewerCommentSidebarProps>;

const isFileIdentifier = (identifier?: Identifier): identifier is Identifier & { id: string } =>
	!!identifier && 'id' in identifier && typeof identifier.id === 'string';

const i18n = defineMessages({
	hideComments: {
		id: 'editor-features.media-viewer.sidebar.hide',
		defaultMessage: 'Hide comments',
		description: 'Accessible label for the button that hides the Media Viewer comments sidebar.',
	},
	showComments: {
		id: 'editor-features.media-viewer.sidebar.show',
		defaultMessage: 'Show comments',
		description: 'Accessible label for the button that shows the Media Viewer comments sidebar.',
	},
	commentsTitle: {
		id: 'editor-features.media-viewer.sidebar.title',
		defaultMessage: 'Comments',
		description: 'Title for the Media Viewer comments sidebar.',
	},
});

type LiveEditorApiRef = MutableRefObject<
	PublicPluginAPI<[MediaPlugin, AnnotationPlugin]> | undefined
>;

// The positions of the mounted media nodes for a file. Filters `mediaNodes` itself because
// `findMediaNode` matches on file id alone, which can pick the wrong copy of a file placed twice.
export const findLiveMediaNodePositions = (
	editorApiRef: LiveEditorApiRef | undefined,
	fileId: string,
	occurrenceKey?: string,
): number[] => {
	const mediaNodes = editorApiRef?.current?.media?.sharedState.currentState()?.mediaNodes ?? [];
	const nodesForFile = mediaNodes.filter(({ node }) => node.attrs?.id === fileId);
	const exactMatches = occurrenceKey
		? nodesForFile.filter(({ node }) => node.attrs?.occurrenceKey === occurrenceKey)
		: [];
	// A caller may have an occurrence key the node attrs don't; with a single placement there
	// is nothing to disambiguate.
	const candidates = exactMatches.length > 0 ? exactMatches : nodesForFile;
	return candidates
		.map(({ getPos }) => getPos())
		.filter((pos): pos is number => typeof pos === 'number');
};

// Resolves the media viewer's selected identifier to the media node it is showing, so the
// Media Viewer comment sidebar can read that node's annotations. View pages
// traverse the rendered ADF document; editor and live pages use the media plugin's mounted nodes.
export function useResolveMediaTarget({
	isViewPage,
	editorApiRef,
	editorActions,
}: {
	isViewPage: boolean;
	editorApiRef?: LiveEditorApiRef;
	editorActions?: EditorActions;
}): (identifier?: Identifier) => MediaTargetResolution {
	const { rendererActions } = useRendererActions();

	const resolveLiveMediaTarget = useCallback(
		(identifier: Identifier & { id: string }): MediaTargetResolution => {
			const positions = findLiveMediaNodePositions(
				editorApiRef,
				identifier.id,
				getOccurrenceKey(identifier),
			);
			if (positions.length > 1) {
				// We can't tell which placement the viewer is showing, and commenting on the wrong
				// one is worse than not offering it.
				return { status: 'ambiguous' };
			}

			// Re-read the node from the live document: the mounted node object is a snapshot that
			// misses marks written since it was registered.
			const [position] = positions;
			const node =
				position === undefined
					? undefined
					: editorActions?._privateGetEditorView()?.state.doc.nodeAt(position);
			const isMediaNode = node?.type.name === 'media' || node?.type.name === 'mediaInline';
			if (!node || !isMediaNode || node.attrs.id !== identifier.id) {
				return { status: 'missing' };
			}

			return {
				status: 'resolved',
				target: {
					fileId: identifier.id,
					annotationIds: node.marks
						.filter(
							(mark) =>
								mark.type.name === 'annotation' &&
								mark.attrs.annotationType === AnnotationTypes.INLINE_COMMENT,
						)
						.map((mark) => mark.attrs.id)
						.filter((id): id is AnnotationId => typeof id === 'string'),
					position,
					displayText:
						node.attrs.alt || node.attrs.__fileName || node.attrs.fileName || node.attrs.name,
					occurrenceKey:
						typeof node.attrs.occurrenceKey === 'string' ? node.attrs.occurrenceKey : undefined,
				},
				isInMediaSingle:
					editorActions?._privateGetEditorView()?.state.doc.resolve(position).parent.type.name ===
					'mediaSingle',
			};
		},
		[editorActions, editorApiRef],
	);

	const resolveViewPageMediaTarget = useCallback(
		(identifier: Identifier & { id: string }): MediaTargetResolution => {
			const result = rendererActions?.getMediaNodeContext({
				id: identifier.id,
				occurrenceKey: getOccurrenceKey(identifier),
			});
			if (!result || result.status !== 'resolved') {
				return result ?? { status: 'missing' };
			}
			return {
				status: 'resolved',
				target: result.context,
				isInMediaSingle:
					rendererActions?.doc?.resolve(result.context.position).parent.type.name === 'mediaSingle',
			};
		},
		[rendererActions],
	);

	const resolveMediaTarget = useCallback(
		(identifier?: Identifier): MediaTargetResolution => {
			if (!isFileIdentifier(identifier)) {
				return { status: 'unsupported' };
			}
			return isViewPage
				? resolveViewPageMediaTarget(identifier)
				: resolveLiveMediaTarget(identifier);
		},
		[isViewPage, resolveLiveMediaTarget, resolveViewPageMediaTarget],
	);

	return resolveMediaTarget;
}

// Builds the media viewer comments sidebar configuration and the persistent viewer state
// callbacks the Media Viewer uses to survive renderer remounts. The sidebar component is passed in
// because @confluence/comments-panel depends back on this package. Returns undefined unless comments
// are enabled for this surface and a sidebar is supplied.
//
// IMPORTANT: pass the returned object down to Media Viewer as-is. Some of its fields are getters that
// read the viewer state when Media Viewer reads them; copying or merging the object
// (e.g. `{ ...extensions }`) reads them once and freezes those values.
// Re-rendering this triggers ADFRenderer / FullPageEditorComponent re-renders, so be judicious
export function useMediaCommentExtensions({
	isViewPage,
	isEnabled,
	CommentSidebar,
	editorApiRef,
	editorActions,
}: {
	isViewPage: boolean;
	isEnabled: boolean;
	CommentSidebar?: MediaViewerCommentSidebarComponent;
	editorApiRef?: LiveEditorApiRef;
	editorActions?: EditorActions;
}): MediaViewerExtensions | undefined {
	const { formatMessage } = useIntl();
	const resolveMediaTarget = useResolveMediaTarget({ isViewPage, editorApiRef, editorActions });
	// Read the dirty state without subscribing: this hook runs on every view page and in the
	// editor, so subscribing would re-render them whenever any comment editor changes.
	const { resetContentChanged } = useCommentsContentActions();
	const { showModal } = useDialogs();

	// While a prompt is open, further exits are ignored so a second prompt can't be queued.
	const isPromptInFlightRef = useRef<boolean>(false);

	// Same guard as the comments panel's `canClosePanel`: `proceed` only runs if there is no unsaved
	// draft or the user confirms discarding it.
	const guardedExit = useCallback(
		(proceed: () => void) => {
			if (!getCommentsContentState().hasContentChanged) {
				proceed();
				return;
			}
			if (isPromptInFlightRef.current) {
				return;
			}
			isPromptInFlightRef.current = true;
			// Discarding resets the store first, so a `proceed` that re-enters this callback doesn't prompt
			// again. The dialog calls `onClose` after Discard, Cancel and any other dismissal.
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					proceed();
				},
				onClose: () => {
					isPromptInFlightRef.current = false;
				},
			});
		},
		[showModal, resetContentChanged],
	);

	const applyLiveMediaAnnotation = useCallback(
		(target: MediaNodeContext, annotationId: string) => {
			if (isViewPage) {
				return false;
			}
			const editorApi = editorApiRef?.current;
			const editorView = editorActions?._privateGetEditorView();
			const setInlineCommentDraftState = editorApi?.annotation?.actions?.setInlineCommentDraftState;
			const applyInlineCommentDraft = editorApi?.annotation?.actions?.applyInlineCommentDraft;
			const positions = findLiveMediaNodePositions(
				editorApiRef,
				target.fileId,
				target.occurrenceKey,
			);
			const mediaPos = positions.length === 1 ? positions[0] : undefined;
			if (
				!editorView ||
				!setInlineCommentDraftState ||
				!applyInlineCommentDraft ||
				typeof mediaPos !== 'number'
			) {
				return false;
			}

			// The draft is derived from the editor selection, so select the media single the viewer is
			// showing rather than whatever was last selected.
			const $mediaPos = editorView.state.doc.resolve(mediaPos);
			if ($mediaPos.parent.type.name !== 'mediaSingle') {
				return false;
			}
			editorView.dispatch(
				editorView.state.tr.setSelection(
					NodeSelection.create(editorView.state.doc, $mediaPos.before()),
				),
			);

			// TODO: Use a distinct InlineCommentInputMethod for Media Viewer. The annotation plugin only
			// allows TOOLBAR | SHORTCUT | FLOATING_TB, so this and the rollback report as a floating toolbar comment.
			const draftStarted = setInlineCommentDraftState(
				true,
				INPUT_METHOD.FLOATING_TB,
				'block',
				target.fileId,
			)(editorView.state, editorView.dispatch);
			if (!draftStarted) {
				return false;
			}

			const applied = applyInlineCommentDraft(annotationId)(editorView.state, editorView.dispatch);
			if (!applied) {
				// Roll back the draft we started above.
				setInlineCommentDraftState(false, INPUT_METHOD.FLOATING_TB)(
					editorView.state,
					editorView.dispatch,
				);
				return false;
			}

			return true;
		},
		[editorActions, editorApiRef, isViewPage],
	);

	const removeLiveMediaAnnotation = useCallback(
		(annotationId: string) => {
			if (isViewPage) {
				return false;
			}
			const editorView = editorActions?._privateGetEditorView();
			const removeInlineCommentAnnotation =
				editorApiRef?.current?.annotation?.actions?.removeInlineCommentAnnotation;
			if (!editorView || !removeInlineCommentAnnotation) {
				return false;
			}
			return removeInlineCommentAnnotation(annotationId)(editorView.state, editorView.dispatch);
		},
		[editorActions, editorApiRef, isViewPage],
	);

	const renderMediaCommentSidebar = useCallback(
		(selectedIdentifier: Identifier) => {
			if (!CommentSidebar) {
				return null;
			}
			return (
				<CommentSidebar
					resolveTarget={resolveMediaTarget}
					keepLastResolvedTarget={!isViewPage}
					selectedIdentifier={selectedIdentifier}
					applyMediaAnnotation={applyLiveMediaAnnotation}
					removeMediaAnnotation={removeLiveMediaAnnotation}
				/>
			);
		},
		[
			CommentSidebar,
			applyLiveMediaAnnotation,
			isViewPage,
			removeLiveMediaAnnotation,
			resolveMediaTarget,
		],
	);

	return useMemo((): MediaViewerExtensions | undefined => {
		// Media that can't be commented on still opens in the inset viewer, without the comments sidebar.
		// Checked without exposure, so only surfaces that can be commented on are exposed.
		if (!isEnabled || !CommentSidebar) {
			return UNSAFE_expValEqualsNoExposure('cc_comments_media_viewer_sidebar', 'isEnabled', true)
				? { useInsetViewer: fg('cc_comments_inset_media_viewer') }
				: undefined;
		}

		if (!expValEquals('cc_comments_media_viewer_sidebar', 'isEnabled', true)) {
			return undefined;
		}

		return {
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			useInsetViewer: fg('cc_comments_inset_media_viewer'),
			// Persist preview owner, selected preview item, and sidebar open/closed
			// across MediaViewer remounts (e.g. when the renderer re-mounts after
			// `updateDocumentFn` writes a new doc after media-comment save/delete).
			// Confluence owns the matching policy: the card opened from a file id hosts
			// the modal, while selectedIdentifier tracks the currently viewed item.
			getMediaViewerSelectedItem: (identifier) => {
				const { viewerOpenFromIdentifier, selectedIdentifier } = getMediaViewerSidebarState();
				if (
					!viewerOpenFromIdentifier ||
					!('id' in identifier) ||
					!('id' in viewerOpenFromIdentifier)
				) {
					return null;
				}
				return identifier.id === viewerOpenFromIdentifier.id &&
					occurrenceKeysMatch(
						getOccurrenceKey(identifier),
						getOccurrenceKey(viewerOpenFromIdentifier),
					)
					? (selectedIdentifier ?? identifier)
					: null;
			},
			onSelectedItemChange: (identifier) => {
				const { viewerOpenFromIdentifier } = getMediaViewerSidebarState();
				setMediaViewerSelectedIdentifier(identifier);
				if (!identifier || !viewerOpenFromIdentifier) {
					setMediaViewerOpenFromIdentifier(identifier);
				}
				// A fresh open starts with the sidebar open only when the opened item has open comments.
				// Navigation and remounts keep the sidebar as it is.
				if (identifier && !viewerOpenFromIdentifier) {
					const resolution = resolveMediaTarget(identifier);
					const { commentsDataMap } = getProcessedCommentsDataState();
					setMediaViewerSidebarOpen(
						resolution.status === 'resolved' &&
							resolution.target.annotationIds.some(
								(annotationId) => commentsDataMap.inline[annotationId]?.isOpen,
							),
					);
				}
			},
			// Getters, so MediaViewer reads the current store value when it mounts or re-renders its
			// header, without this hook subscribing to the store and re-rendering the page on every change.
			get defaultSidebarVisible() {
				const { isSidebarOpen } = getMediaViewerSidebarState();
				return isSidebarOpen;
			},
			onSidebarVisibilityChange: setMediaViewerSidebarOpen,
			// Ask before discarding an unsaved comment draft on close or navigation. The viewer state only
			// has to survive remounts, which never call `onPreviewClose`, so a real close resets it.
			onPreviewClose: (proceed) =>
				guardedExit(() => {
					resetMediaViewerSidebar();
					proceed();
				}),
			onSidebarClose: (proceed) => guardedExit(proceed),
			onNavigation: (_direction, proceed) => guardedExit(proceed),
			// A getter, like the ones above, so it answers for the item showing without this hook
			// subscribing to the store. Only media in a mediaSingle, such as images and videos, can be
			// commented on; a file card, such as a zip, gets no sidebar.
			get sidebar() {
				const { isSidebarOpen, selectedIdentifier } = getMediaViewerSidebarState();
				const resolution = resolveMediaTarget(selectedIdentifier ?? undefined);
				// Only resolved targets have a parent to check.
				if (resolution.status !== 'resolved' || !resolution.isInMediaSingle) {
					return undefined;
				}
				return {
					icon: <CommentIcon label="" spacing="spacious" />,
					label: formatMessage(isSidebarOpen ? i18n.hideComments : i18n.showComments),
					title: formatMessage(i18n.commentsTitle),
					renderer: renderMediaCommentSidebar,
				};
			},
		};
	}, [
		isEnabled,
		CommentSidebar,
		renderMediaCommentSidebar,
		resolveMediaTarget,
		formatMessage,
		guardedExit,
	]);
}
